/**
 * Persona Slicer (doc 147 §6.4) — preset compliance lenses applied on top of
 * the existing ConstellationFilters. The dialog reads `PERSONA_PRESETS`,
 * applies the chosen filter patch, and surfaces a breakdown of what the
 * change hides so engineers can dress-rehearse a release for a given persona
 * before shipping it.
 *
 * Pure module: presets are static data, `slicerBreakdown` is a deterministic
 * function over (manifest, filters) — no DOM or hooks involved.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { applyFilters, type ConstellationFilters } from './filters.js';

export interface PersonaPreset {
  id: string;
  label: string;
  description: string;
  /** Filter patch merged on top of the current filters. */
  filters: Partial<ConstellationFilters>;
}

export const PERSONA_PRESETS: readonly PersonaPreset[] = [
  {
    id: 'kid-teen',
    label: 'Kid / Teen (under 18)',
    description: 'Hide every entity not explicitly marked minor-visible. The spec’s primary compliance dress-rehearsal.',
    filters: { isMinor: 'visible' },
  },
  {
    id: 'teen-plus',
    label: 'Teen+ (16–17)',
    description: 'Same minor-only floor as Kid/Teen but with lending intentionally NOT hidden — used to validate the 16+ lending preview path.',
    filters: { isMinor: 'visible' },
  },
  {
    id: 'pro-only',
    label: 'Pro tier only',
    description: 'Show only entities gated to the Pro subscription tier.',
    filters: { tier: 'pro' },
  },
  {
    id: 'investor-pack',
    label: 'Investor pack only',
    description: 'Show only entities in domains gated by the Investor pack.',
    filters: { pack: 'investor' },
  },
];

export function presetById(id: string): PersonaPreset | undefined {
  return PERSONA_PRESETS.find((p) => p.id === id);
}

export interface DomainBreakdown {
  domainId: string;
  domainName: string;
  visible: number;
  hidden: number;
}

export interface SlicerBreakdown {
  totalEntities: number;
  entitiesVisible: number;
  entitiesHidden: number;
  totalSurfaces: number;
  surfacesVisible: number;
  surfacesHidden: number;
  perDomain: DomainBreakdown[];
  /** Human-readable warnings (e.g. "all entities hidden"). */
  warnings: string[];
}

/**
 * Compute a breakdown of what a filter set hides vs reveals — the data the
 * dialog renders. Pure over (manifest, filters); cheap enough to call on every
 * filter change.
 */
export function slicerBreakdown(
  manifest: AtlasManifest,
  filters: ConstellationFilters,
): SlicerBreakdown {
  const visible = applyFilters(manifest, filters);
  const totalEntities = manifest.entities.length;
  const entitiesVisible = visible.size;
  const entitiesHidden = totalEntities - entitiesVisible;

  // Surfaces “disappear” when none of the entities that link to them remain
  // visible. For surfaces with no entity backlinks the surface stays visible
  // (we can’t tell what hides it).
  let surfacesVisible = 0;
  for (const surface of manifest.surfaces) {
    const usingEntities = manifest.entities.filter((e) => e.surfaces.includes(surface.id));
    if (usingEntities.length === 0) {
      surfacesVisible += 1;
    } else if (usingEntities.some((e) => visible.has(e.id))) {
      surfacesVisible += 1;
    }
  }
  const totalSurfaces = manifest.surfaces.length;
  const surfacesHidden = totalSurfaces - surfacesVisible;

  // Per-domain visible vs hidden counts.
  const perDomain: DomainBreakdown[] = manifest.domains.map((d) => {
    const inDomain = manifest.entities.filter((e) => e.domain === d.id);
    const vis = inDomain.filter((e) => visible.has(e.id)).length;
    return {
      domainId: d.id,
      domainName: d.name,
      visible: vis,
      hidden: inDomain.length - vis,
    };
  });

  const warnings: string[] = [];
  if (entitiesHidden === totalEntities && totalEntities > 0) {
    warnings.push('All entities are hidden by the current filters — this rarely matches an intended persona.');
  } else if (entitiesHidden > totalEntities * 0.7) {
    warnings.push(
      `${entitiesHidden} of ${totalEntities} entities (${Math.round((entitiesHidden / totalEntities) * 100)}%) are hidden — verify this matches the persona’s expected scope.`,
    );
  }
  if (surfacesHidden > 0) {
    warnings.push(
      `${surfacesHidden} of ${totalSurfaces} surfaces will not render for this persona.`,
    );
  }

  return {
    totalEntities,
    entitiesVisible,
    entitiesHidden,
    totalSurfaces,
    surfacesVisible,
    surfacesHidden,
    perDomain,
    warnings,
  };
}
